import { DatePipe } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { AlertService } from '@context/frontend/alert';
import { DialogService } from '@context/frontend/common';
import { getUaDevice, IsDevicePipe } from '@context/frontend/native/platform';
import {
	AcknowledgeDialogComponent,
	DetailMenuDialogComponent,
	DetailMenuDropdownComponent,
} from '@context/frontend/ui/components';
import {
	Chemical,
	ChemicalRevision,
	ChemicalUsageLog,
} from '@context/shared/types/chemical';
import { DetailMenuItem } from '@context/shared/types/common';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiButton, TuiDropdown, TuiHint, TuiIcon } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { DocumentReference, getDoc } from 'firebase/firestore';
import { ChemicalUsageLogDetailsDialogComponent } from '../../dialogs';
import { ChemicalUsageLogService } from '../../services/chemical-usage-log.service';
import { ChemicalReiBadgeComponent } from '../chemical-rei-badge/chemical-rei-badge.component';

@Component({
	imports: [
		TranslocoModule,
		TuiIcon,
		TuiButton,
		TuiHint,
		TuiDropdown,
		DetailMenuDropdownComponent,
		IsDevicePipe,
		DatePipe,
		ChemicalReiBadgeComponent,
	],
	selector: 'ctx-chemical-usage-log-row',
	templateUrl: 'chemical-usage-log-row.component.html',
	styleUrl: 'chemical-usage-log-row.component.scss',
	host: {
		'[attr.device]': 'device',
	},
})
export class ChemicalUsageLogRowComponent {
	readonly device = getUaDevice();

	private readonly dialog = inject(DialogService);
	private readonly alert = inject(AlertService);
	private readonly usageLogService = inject(ChemicalUsageLogService);

	readonly usageLog = input.required<ChemicalUsageLog>();
	readonly usageLogRef =
		input.required<DocumentReference<ChemicalUsageLog>>();
	/** When true, hide delete and show only view-details (e.g. for lobby/companion app). */
	readonly readOnly = input(false);

	readonly update = output<void>();

	readonly chemical = signal<Chemical | null>(null);
	readonly chemicalRevision = signal<ChemicalRevision | null>(null);
	readonly loading = signal(true);

	menuOpen = false;

	readonly appliedAtDate = computed(() => {
		const log = this.usageLog();
		return log.appliedAt?.toDate() ?? null;
	});

	readonly reiDate = computed(() => {
		const log = this.usageLog();

		const appliedAt = this.appliedAtDate();
		if (!appliedAt) return null;

		const reiHours = log.reiHours;
		appliedAt.setHours(appliedAt.getHours() + reiHours);
		return appliedAt;
	});

	readonly chemicalTooltip = computed(() => {
		const chemical = this.chemical();
		const revision = this.chemicalRevision();

		if (!chemical) return null;

		const parts: string[] = [`<b>${chemical.name}</b>`];

		if (revision?.regulationNumber) {
			parts.push(
				`<b>Regulation Number:</b> ${revision.regulationNumber}`,
			);
		}

		if (chemical.activeIngredient) {
			parts.push(
				`<b>Active Ingredient:</b> ${chemical.activeIngredient}`,
			);
		}

		return parts.join('\n');
	});

	readonly menuItems = computed<DetailMenuItem[]>(() => {
		if (this.readOnly()) {
			return [
				{
					text: 'chemical.usage-log.view-details',
					iconStart: '@tui.info',
					handler: () => this.onViewDetails(),
				},
			];
		}

		return [
			{
				text: 'chemical.usage-log.view-details',
				iconStart: '@tui.info',
				handler: () => this.onViewDetails(),
			},
			{
				text: 'action.delete',
				iconStart: '@tui.trash',
				handler: () => this.onDelete(),
			},
		];
	});

	readonly usageLogDetailDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(ChemicalUsageLogDetailsDialogComponent),
			ChemicalUsageLogDetailsDialogComponent.Options({
				data: { usageLog: this.usageLog(), showInternalNote: true },
			}),
		);

	readonly deleteDialog = (chemical: string, location: string) =>
		this.dialog.open(
			new PolymorpheusComponent(AcknowledgeDialogComponent),
			AcknowledgeDialogComponent.Options({
				label: 'chemical.usage-log.delete-header',
				data: {
					description: 'chemical.usage-log.delete-description',
					acknowledge: 'chemical.usage-log.delete-acknowledge',
					translationParams: { chemical, location },
				},
			}),
		);

	readonly usageLogMenuDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(DetailMenuDialogComponent),
			DetailMenuDialogComponent.Options({
				label: 'chemical.usage-log.view-details',
				data: { menuItems: this.menuItems() },
			}),
		);

	constructor() {
		effect(() => {
			const log = this.usageLog();
			if (!log) return;
			this.loadChemical(log);
		});
	}

	private async loadChemical(log: ChemicalUsageLog) {
		const currentChemical = this.chemical();
		const currentRevision = this.chemicalRevision();
		if (
			currentChemical?.id === log.chemical.id &&
			currentRevision?.id === log.chemicalRevision.id
		) {
			return;
		}

		this.loading.set(true);

		try {
			const [chemicalDoc, revisionDoc] = await Promise.all([
				getDoc(log.chemical as DocumentReference<Chemical>),
				getDoc(
					log.chemicalRevision as DocumentReference<ChemicalRevision>,
				),
			]);

			if (chemicalDoc.exists()) {
				this.chemical.set(chemicalDoc.data() as Chemical);
			}

			if (revisionDoc.exists()) {
				this.chemicalRevision.set(
					revisionDoc.data() as ChemicalRevision,
				);
			}
		} catch (error) {
			console.error('Error loading chemical:', error);
		} finally {
			this.loading.set(false);
		}
	}

	readonly onViewDetails = () => this.usageLogDetailDialog().subscribe();

	readonly onMenuOpen = () => this.usageLogMenuDialog().subscribe();

	readonly onDelete = () => {
		const usageLog = this.usageLog();
		const chemicalName = this.chemical()?.name ?? '';

		this.deleteDialog(chemicalName, usageLog.location).subscribe({
			next: (confirmed) => {
				if (!confirmed) return;

				this.usageLogService
					.delete(usageLog.id)
					.then(() => {
						this.alert.open('generic-success').subscribe();
						this.update.emit();
					})
					.catch((error) => {
						console.error(
							'There was an issue deleting the usage log',
							error,
						);
						this.alert.open('generic-error').subscribe();
					});
			},
		});
	};
}
